import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule,ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle,MatSlideToggleChange } from '@angular/material/slide-toggle';
import { QuillModule, QuillModules } from 'ngx-quill';
import { Subscription } from 'rxjs';

import { TKeyword } from '@core/interfaces/Endpoints';

import { defaultDashboardContent } from '../../../../../constants/defaults';
import { CommonService } from '@shared/services/common.service';
import { KeywordsService, SelectedKeyword } from '@shared/services/keywords.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';

@Component({
    selector: 'app-dashboard-content',
    templateUrl: './dashboard-content.component.html',
    styleUrls: ['./dashboard-content.component.scss'],
    standalone: true,
    imports: [MatSlideToggle, MatProgressSpinner, MatIcon, QuillModule, ReactiveFormsModule, FormsModule, MatButton]
})
export class DashboardContentComponent implements OnInit, OnDestroy {
  protected htmlContent: string | null = null;
  protected isLogoVisible = true;
  protected availableKeywords: TKeyword[] = [];
  protected selectedKeywords: SelectedKeyword[] = [];
  protected isKeywordsLoading = false;
  private _subs: Subscription[] = [];
  protected quillModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['link'],
      ['clean']
    ]
  };

  constructor(
    private _metadataService: MetadataService,
    private _commonService: CommonService,
    private _keywordsService: KeywordsService
  ) {}

  ngOnInit(): void {
    this.getContent();
    this.getLogoVisibility();
    this.loadKeywords();
    this.getSelectedKeywords();
  }

  getContent() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardContent).subscribe({
        next: metadata => {
          if (metadata.value) {
            this.htmlContent = metadata.value;
          } else {
            this.htmlContent = defaultDashboardContent;
          }
        },
        error: () => {
          this.htmlContent = defaultDashboardContent;
        },
      })
    );
  }

  getLogoVisibility() {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardLogoVisible).subscribe({
        next: metadata => {
          this.isLogoVisible = metadata.value ? metadata.value === 'true' : true;
        },
        error: () => {
          this.isLogoVisible = true;
        },
      })
    );
  }

  onLogoVisibilityChange(event: MatSlideToggleChange) {
    this._subs.push(
      this._metadataService
        .set(MetadataSlugs.DashboardLogoVisible, event.checked.toString())
        .subscribe({
          next: () => {
            this._commonService.openSuccessSnackBar('Logo visibility updated!');
          },
          error: () => {
            this._commonService.openErrorSnackBar('Error updating logo visibility');
          },
        })
    );
  }

  save() {
    // Save both content and keywords
    const contentSave = this._metadataService.set(MetadataSlugs.DashboardContent, this.htmlContent);
    const keywordsSave = this._metadataService.set(
      MetadataSlugs.DashboardKeywords,
      JSON.stringify(this.selectedKeywords)
    );

    this._subs.push(
      contentSave.subscribe({
        next: () => {
          this._commonService.openSuccessSnackBar('Dashboard content saved successfully!');
        },
        error: () => {
          this._commonService.openErrorSnackBar('Error saving dashboard content');
        },
      })
    );

    this._subs.push(
      keywordsSave.subscribe({
        next: () => {
          // Keywords saved - update service
          this._keywordsService.setSelectedKeywords(this.selectedKeywords);
        },
        error: () => {
          this._commonService.openErrorSnackBar('Error saving keywords');
        },
      })
    );
  }

  loadKeywords(): void {
    this.isKeywordsLoading = true;
    this._subs.push(
      this._keywordsService.fetchKeywords().subscribe({
        next: keywords => {
          this.availableKeywords = keywords;
          this.isKeywordsLoading = false;

          // Log info about keywords availability for debugging
          if (keywords.length === 0) {
            console.info('No keywords available from backend - keywords feature will be hidden');
          } else {
            console.info(`Loaded ${keywords.length} keywords for dashboard configuration`);
          }
        },
        error: () => {
          // This error handler is now mainly for network issues, not API errors
          // API errors are handled in the service with graceful degradation
          this.availableKeywords = [];
          this.isKeywordsLoading = false;
          console.warn('Network error while loading keywords - feature disabled');
        },
      })
    );
  }

  getSelectedKeywords(): void {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardKeywords).subscribe({
        next: metadata => {
          if (metadata.value) {
            try {
              this.selectedKeywords = JSON.parse(metadata.value) as SelectedKeyword[];
            } catch (error) {
              console.error('Error parsing selected keywords:', error);
              this.selectedKeywords = [];
            }
          } else {
            this.selectedKeywords = [];
          }
        },
        error: () => {
          this.selectedKeywords = [];
        },
      })
    );
  }

  toggleKeyword(keyword: TKeyword): void {
    const isSelected = this.selectedKeywords.some(k => k.keyword === keyword.keyword);

    if (isSelected) {
      this.selectedKeywords = this.selectedKeywords.filter(k => k.keyword !== keyword.keyword);
    } else {
      if (this.selectedKeywords.length < 4) {
        this.selectedKeywords.push({
          keyword: keyword.keyword,
          displayText: keyword.keyword,
        });
      } else {
        this._commonService.openErrorSnackBar('Maximum of 4 keywords can be selected');
      }
    }
  }

  isKeywordSelected(keyword: TKeyword): boolean {
    return this.selectedKeywords.some(k => k.keyword === keyword.keyword);
  }

  canSelectMoreKeywords(): boolean {
    return this.selectedKeywords.length < 4;
  }

  get shouldShowKeywordsSection(): boolean {
    // Always show the section after loading is complete
    // This way users can see the informative message if the endpoint is not available
    return !this.isKeywordsLoading;
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }
}
